import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useChannelData } from '../context/ChannelDataContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] },
});

const languages = [
  { nameFr: 'Français', nameEn: 'French', code: 'fra', flagCountryCode: 'fr' },
  { nameFr: 'Anglais', nameEn: 'English', code: 'eng', flagCountryCode: 'gb' },
  { nameFr: 'Arabe', nameEn: 'Arabic', code: 'ara', flagCountryCode: 'sa' },
  { nameFr: 'Espagnol', nameEn: 'Spanish', code: 'spa', flagCountryCode: 'es' },
  { nameFr: 'Russe', nameEn: 'Russian', code: 'rus', flagCountryCode: 'ru' },
  { nameFr: 'Allemand', nameEn: 'German', code: 'deu', flagCountryCode: 'de' },
  { nameFr: 'Italien', nameEn: 'Italian', code: 'ita', flagCountryCode: 'it' },
  { nameFr: 'Chinois', nameEn: 'Chinese', code: 'zho', flagCountryCode: 'cn' },
  { nameFr: 'Japonais', nameEn: 'Japanese', code: 'jpn', flagCountryCode: 'jp' },
];

const categoryImageByKey = {
  // Use deterministic picsum seeds for reliable loading + realistic photos.
  all: 'https://picsum.photos/seed/tv-all-realistic/960/640',
  animation: 'https://picsum.photos/seed/tv-animation-realistic/960/640',
  auto: 'https://picsum.photos/seed/tv-auto-realistic/960/640',
  business: 'https://picsum.photos/seed/tv-business-realistic/960/640',
  classic: 'https://picsum.photos/seed/tv-classic-realistic/960/640',
  comedy: 'https://picsum.photos/seed/tv-comedy-realistic/960/640',
  cooking: 'https://picsum.photos/seed/tv-cooking-realistic/960/640',
  culture: 'https://picsum.photos/seed/tv-culture-realistic/960/640',
  documentary: 'https://picsum.photos/seed/tv-documentary-realistic/960/640',
  education: 'https://picsum.photos/seed/tv-education-realistic/960/640',
  entertainment: 'https://picsum.photos/seed/tv-entertainment-realistic/960/640',
  family: 'https://picsum.photos/seed/tv-family-realistic/960/640',
  general: 'https://picsum.photos/seed/tv-general-realistic/960/640',
  kids: 'https://picsum.photos/seed/tv-kids-realistic/960/640',
  legislative: 'https://picsum.photos/seed/tv-legislative-realistic/960/640',
  lifestyle: 'https://picsum.photos/seed/tv-lifestyle-realistic/960/640',
  movies: 'https://picsum.photos/seed/tv-movies-realistic/960/640',
  music: 'https://picsum.photos/seed/tv-music-realistic/960/640',
  news: 'https://picsum.photos/seed/tv-news-realistic/960/640',
  outdoor: 'https://picsum.photos/seed/tv-outdoor-realistic/960/640',
  public: 'https://picsum.photos/seed/tv-public-realistic/960/640',
  relax: 'https://picsum.photos/seed/tv-relax-realistic/960/640',
  religious: 'https://picsum.photos/seed/tv-religious-realistic/960/640',
  science: 'https://picsum.photos/seed/tv-science-realistic/960/640',
  series: 'https://picsum.photos/seed/tv-series-realistic/960/640',
  shop: 'https://picsum.photos/seed/tv-shop-realistic/960/640',
  show: 'https://picsum.photos/seed/tv-show-realistic/960/640',
  sports: 'https://picsum.photos/seed/tv-sports-realistic/960/640',
  'top-news': 'https://picsum.photos/seed/tv-top-news-realistic/960/640',
  travel: 'https://picsum.photos/seed/tv-travel-realistic/960/640',
  weather: 'https://picsum.photos/seed/tv-weather-realistic/960/640',
};

const categoryPriorityOrder = ['religious', 'movies', 'music', 'sports', 'comedy', 'business'];

function channelLanguageMatches(channel, canonicalCode) {
  const codes = [];
  if (channel?.language) codes.push(channel.language);
  if (Array.isArray(channel?.languages)) codes.push(...channel.languages);
  return codes.some((lang) => {
    if (!lang) return false;
    if (lang === canonicalCode) return true;
    if (canonicalCode === 'zho' && (lang === 'cmn' || lang === 'yue' || lang === 'nan')) return true;
    return false;
  });
}

function SectionHeader({ eyebrow, title, description, action }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:mb-8 sm:gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="max-w-2xl">
        {eyebrow ? <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-400/90">{eyebrow}</p> : null}
        <h2 className="mt-1.5 text-xl font-bold tracking-tight text-white sm:mt-2 sm:text-3xl">{title}</h2>
        {description ? <p className="mt-1.5 text-sm leading-relaxed text-slate-500 sm:mt-2 sm:text-base">{description}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

function ChannelPreviewMedia({ logo, t }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = Boolean(logo) && !logoFailed;

  if (showLogo) {
    return (
      <img
        src={logo}
        alt=""
        className="max-h-full max-w-full object-contain p-2.5 transition duration-300 group-hover:scale-[1.03] sm:p-3"
        onError={() => setLogoFailed(true)}
      />
    );
  }

  return (
    <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_center,rgba(37,99,235,0.22),rgba(2,6,23,0.9)_58%)]">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-600/80 bg-slate-800/85 text-brand-300 shadow-lg ring-1 ring-white/10 sm:h-12 sm:w-12">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" className="h-5 w-5 sm:h-6 sm:w-6" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
          <circle cx="12" cy="12" r="9" />
        </svg>
      </div>
      <span className="absolute bottom-2 left-2 rounded-md border border-slate-700/90 bg-slate-950/85 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-300">
        {t('Aperçu vidéo', 'Video preview')}
      </span>
    </div>
  );
}

const LandingPage = ({ setHighLightedCountry, setSelectedCountry, channels, setChannels, setSelectedChannel, setSearchTerm, recentChannels = [] }) => {
  const { allCategories, refresh, loading, memoizedAllChannels, countriesMetadata, error, channelsUpdatedAt } = useChannelData();
  const { language } = useLanguage();
  const nav = useNavigate();
  const isFr = language === 'fr';
  const t = (frText, enText) => (isFr ? frText : enText);
  const [showAllLanguages, setShowAllLanguages] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);

  const stats = useMemo(() => {
    const totalChannels = memoizedAllChannels?.length ?? 0;
    const regions = Object.values(countriesMetadata || {}).filter((m) => m?.hasChannels).length;
    const categoryKeys = Object.keys(allCategories || {}).filter(
      (k) => k !== 'all-channels' && k !== 'all'
    );
    return {
      totalChannels,
      regions,
      categoryCount: categoryKeys.length,
    };
  }, [memoizedAllChannels, countriesMetadata, allCategories]);

  const featuredFlags = useMemo(() => {
    const keys = Object.keys(countriesMetadata || {}).filter((code) => countriesMetadata?.[code]?.hasChannels);
    return keys.slice(0, 8).map((code) => ({
      code: code.toUpperCase(),
      src: `https://flagcdn.com/${String(code).toLowerCase()}.svg`,
    }));
  }, [countriesMetadata]);

  const updatedLabel = useMemo(() => {
    if (!channelsUpdatedAt) return null;
    try {
      return format(new Date(channelsUpdatedAt), 'd MMM yyyy · HH:mm', isFr ? { locale: fr } : undefined);
    } catch {
      return null;
    }
  }, [channelsUpdatedAt, isFr]);

  useEffect(() => {
    setSearchTerm?.('');
    setHighLightedCountry?.(null);
    let cancelled = false;
    fetch('https://www.cloudflare.com/cdn-cgi/trace')
      .then((res) => res.text())
      .then((text) => {
        if (cancelled) return;
        const match = text.match(/loc=(\w+)/);
        if (match) {
          const code = match[1].toLowerCase();
          setSelectedCountry?.(code);
          setHighLightedCountry?.(code);
        }
      })
      .catch((err) => console.error('Error fetching location:', err));
    return () => {
      cancelled = true;
    };
  }, [setHighLightedCountry, setSearchTerm, setSelectedCountry]);

  const onEnter = () => nav('/all-countries');
  const onEvent = () => nav('/events');
  const openAllChannels = () => {
    setSelectedCountry?.(null);
    setHighLightedCountry?.(null);
    setChannels?.(memoizedAllChannels || []);
    nav('/channels');
  };
  const openPlayerWithSample = () => {
    const source = channels?.length > 0 ? channels : memoizedAllChannels || [];
    const playable = source.find(
      (c) => (Array.isArray(c?.iptv_urls) && c.iptv_urls.length > 0) || (Array.isArray(c?.youtube_urls) && c.youtube_urls.length > 0)
    );

    if (!playable) {
      openAllChannels();
      return;
    }

    setSelectedChannel?.(playable);
    setSelectedCountry?.(null);
    setHighLightedCountry?.(null);
    nav('/player');
  };

  const openRecentChannel = (channel) => {
    setSelectedChannel?.(channel);
    nav('/player');
  };

  const scrollToHow = () => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });

  const openLanguage = (code) => {
    const filterByLang = (memoizedAllChannels || []).filter((c) => channelLanguageMatches(c, code));
    setSelectedCountry?.(null);
    setHighLightedCountry?.(null);
    setChannels?.(filterByLang);
    nav('/channels');
  };

  const openCategory = (category) => {
    setSelectedCountry?.(null);
    setHighLightedCountry?.(null);
    setChannels?.(allCategories[category] || []);
    nav('/channels');
  };

  const formatCategoryLabel = (key) =>
    key
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const curatedCategoryCards = useMemo(() => {
    const cards = Object.keys(allCategories || {})
      .filter((key) => key !== 'all-channels')
      .map((key) => ({
        key,
        label: formatCategoryLabel(key),
        image: categoryImageByKey[key] || `https://picsum.photos/seed/tv-${key}/960/640`,
        count: Array.isArray(allCategories[key]) ? allCategories[key].length : 0,
      }));

    const priorityIndex = new Map(categoryPriorityOrder.map((key, idx) => [key, idx]));
    return cards.sort((a, b) => {
      const aIndex = priorityIndex.has(a.key) ? priorityIndex.get(a.key) : Number.POSITIVE_INFINITY;
      const bIndex = priorityIndex.has(b.key) ? priorityIndex.get(b.key) : Number.POSITIVE_INFINITY;
      if (aIndex !== bIndex) return aIndex - bIndex;
      return 0;
    });
  }, [allCategories]);

  const visibleLanguages = useMemo(
    () => (showAllLanguages ? languages : languages.slice(0, 4)),
    [showAllLanguages]
  );

  const visibleCategoryCards = useMemo(
    () => (showAllCategories ? curatedCategoryCards : curatedCategoryCards.slice(0, 6)),
    [showAllCategories, curatedCategoryCards]
  );

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-slate-950">
      <div className="pointer-events-none absolute inset-0 bg-mesh-gradient" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,transparent_0%,rgba(2,6,23,0.4)_45%,rgba(2,6,23,0.92)_100%)]" />

      <header className="sticky top-0 z-50 border-b border-slate-800/80 bg-slate-950/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[3.5rem] max-w-7xl items-center justify-between gap-3 px-4 sm:h-16 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => nav('/')}
            className="flex min-w-0 items-center gap-2.5 text-left transition hover:opacity-90"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-indigo-600 text-xs font-bold text-white shadow-glow ring-1 ring-white/10">
              AT
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block truncate text-sm font-semibold tracking-tight text-white">ASVA TV</span>
              <span className="hidden text-[11px] font-medium text-slate-500 sm:block">{t('Découverte de chaînes', 'Channel discovery')}</span>
            </span>
          </button>

          <nav className="flex items-center gap-1.5 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden sm:gap-2" aria-label="Principal">
            <button type="button" onClick={onEnter} className="rounded-lg px-3 py-2 text-xs font-medium text-slate-300 transition hover:bg-slate-800 hover:text-white sm:px-3 sm:text-sm">
              {t('Parcourir', 'Browse')}
            </button>
            <button type="button" onClick={onEvent} className="rounded-lg px-3 py-2 text-xs font-medium text-slate-300 transition hover:bg-slate-800 hover:text-white sm:px-3 sm:text-sm">
              {t('Événements', 'Events')}
            </button>
            <div className="ml-1 hidden border-l border-slate-800 pl-2 sm:ml-2 sm:block sm:pl-3">
              <span className="rounded-lg px-2 py-2 text-xs font-medium text-slate-500 sm:px-3 sm:text-sm">
                {t('Mode invité', 'Guest mode')}
              </span>
            </div>
            <LanguageSwitcher />
          </nav>
        </div>
      </header>

      {error ? (
        <div className="relative z-10 border-b border-amber-900/40 bg-amber-950/30 px-4 py-3 text-center text-sm text-amber-100/95 sm:px-6">
          {t("Impossible d'actualiser l'index complet. Affichage des données en cache ou partielles.", 'Could not refresh the full index. Showing cached or partial data.')} {' '}
          <button type="button" className="font-semibold text-brand-300 underline-offset-2 hover:underline" onClick={() => refresh()}>
            {t('Réessayer', 'Retry')}
          </button>
        </div>
      ) : null}

      <main className="relative z-10 mx-auto max-w-7xl px-4 pb-12 pt-5 sm:px-6 sm:pb-20 sm:pt-12 lg:px-8 lg:pt-16">
        {/* Hero */}
        <div className="grid items-center gap-6 sm:gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] lg:gap-16">
          <div className="order-1">
            <motion.p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-400/90 sm:text-xs sm:tracking-[0.22em]" {...fadeUp(0)}>
              {t('Carte du monde · TV en direct', 'World map · Live TV')}
            </motion.p>
            <motion.h1
              className="mt-2.5 text-pretty text-[1.75rem] font-bold tracking-tight text-white sm:mt-4 sm:text-5xl lg:text-[3.25rem] lg:leading-[1.1]"
              {...fadeUp(0.04)}
            >
              {t('La télé du monde, sans le bruit des menus', 'World TV without menu overload')}
            </motion.h1>
            <motion.p className="mt-3.5 max-w-xl text-pretty text-sm leading-relaxed text-slate-400 sm:mt-5 sm:text-lg" {...fadeUp(0.08)}>
              {t(
                'Choisissez un pays ou une langue, parcourez les catégories, puis lancez la lecture dans un lecteur épuré. Aucun compte obligatoire—juste l\'envie de zapper le monde.',
                'Pick a country or language, skim categories, then play in a focused player. No account required—just curiosity about what is on elsewhere.'
              )}
            </motion.p>

            <motion.div className="mt-5 flex flex-col gap-2.5 sm:mt-9 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3" {...fadeUp(0.12)}>
              <button type="button" onClick={onEnter} className="ui-btn-primary min-h-[44px] px-6 text-sm shadow-glow sm:min-h-[48px] sm:px-8 sm:text-base">
                {t("Ouvrir l'explorateur de pays", 'Open country browser')}
              </button>
              <button type="button" onClick={onEvent} className="ui-btn-secondary min-h-[44px] px-6 text-sm sm:min-h-[48px] sm:px-8 sm:text-base">
                {t('Événements programmés', 'Scheduled events')}
              </button>
            </motion.div>
            <motion.div className="mt-3.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-500 sm:mt-5 sm:gap-x-6 sm:gap-y-2 sm:text-sm" {...fadeUp(0.16)}>
              <button type="button" onClick={scrollToHow} className="font-medium text-brand-400/90 transition hover:text-brand-300">
                {t('Comment ça marche ↓', 'How it works ↓')}
              </button>
              {updatedLabel ? <span className="hidden sm:inline">{t('Index mis à jour', 'Index updated')} {updatedLabel}</span> : null}
            </motion.div>
          </div>

          {/* Hero preview card */}
          <motion.div
            className="ui-panel order-2 relative overflow-hidden p-0.5 sm:p-1.5 lg:order-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-brand-500/10 blur-3xl" />
            <div className="relative rounded-lg border border-slate-800/80 bg-slate-950/80 p-3.5 shadow-inner sm:rounded-xl sm:p-5">
              <div className="flex items-center justify-between gap-3 border-b border-slate-800/80 pb-4">
                <div>
                  <p className="text-xs font-medium text-slate-500">{t("Aperçu de l'index", 'Index snapshot')}</p>
                  <p className="text-sm font-semibold text-white">{t('Catalogue des chaînes', 'Channel catalog')}</p>
                </div>
                <span className="rounded-full bg-emerald-950/80 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-400 ring-1 ring-emerald-800/50">
                  {t('Données en direct', 'Live data')}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <div className="rounded-lg border border-slate-800 bg-slate-900/80 p-3">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500">{t('Chaînes', 'Channels')}</p>
                  <p className="mt-1 text-xl font-bold tabular-nums text-white">{loading ? '—' : stats.totalChannels.toLocaleString()}</p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/80 p-3">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500">{t('Régions', 'Regions')}</p>
                  <p className="mt-1 text-xl font-bold tabular-nums text-white">{loading ? '—' : stats.regions.toLocaleString()}</p>
                </div>
                <div className="rounded-lg border border-slate-800 bg-slate-900/80 p-3">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-slate-500">{t('Listes', 'Lists')}</p>
                  <p className="mt-1 text-xl font-bold tabular-nums text-white">{loading ? '—' : stats.categoryCount.toLocaleString()}</p>
                </div>
              </div>
              <div className="mt-4 rounded-lg border border-slate-800/80 bg-slate-900/40 px-3 py-3">
                <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Drapeaux des pays</p>
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {featuredFlags.map((flag) => (
                    <div key={flag.code} className="flex h-9 items-center justify-center rounded-md border border-slate-700/70 bg-slate-950/60 p-1.5">
                      <img src={flag.src} alt="" className="h-full w-auto rounded-sm ring-1 ring-black/20" loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={onEnter}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-800/50 py-3 text-sm font-semibold text-white transition hover:border-brand-500/40 hover:bg-slate-800"
              >
                Lancer l'explorateur
                <span aria-hidden>→</span>
              </button>
            </div>
          </motion.div>
        </div>

        {/* Recently viewed */}
        {recentChannels.length > 0 ? (
          <section className="mt-12 border-t border-slate-800/60 pt-10 sm:mt-16 sm:pt-12">
            <SectionHeader
              eyebrow={t('Historique', 'History')}
              title={t('Dernières chaînes consultées', 'Last viewed channels')}
              description={t('Retrouvez rapidement les chaînes ouvertes récemment et relancez le lecteur en un clic.', 'Quickly return to channels you opened recently and restart playback in one tap.')}
            />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              {recentChannels.slice(0, 10).map((channel, idx) => (
                <button
                  key={`${channel.name || 'recent'}-${idx}`}
                  type="button"
                  onClick={() => openRecentChannel(channel)}
                  className="group rounded-xl border border-slate-800/80 bg-slate-900/45 p-3 text-left transition hover:border-brand-500/35 hover:bg-slate-900/80"
                >
                  <div className="flex aspect-[16/10] items-center justify-center overflow-hidden rounded-lg bg-slate-900 ring-1 ring-slate-700/80">
                    {channel.logo ? (
                      <img src={channel.logo} alt="" className="h-full w-full object-contain p-2" loading="lazy" />
                    ) : (
                      <span className="text-sm font-bold text-brand-400">TV</span>
                    )}
                  </div>
                  <p className="mt-2 line-clamp-2 text-xs font-semibold leading-snug text-slate-100 sm:text-sm">{channel.name}</p>
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {/* Regional suggestions */}
        <section className="mt-12 border-t border-slate-800/60 pt-10 sm:mt-20 sm:pt-16">
          <SectionHeader
            eyebrow={t('Pour vous', 'For you')}
            title={t('Chaînes proches de votre région', 'Channels near your region')}
            description={t('Nous utilisons un indice de localisation léger pour précharger une courte liste. La lecture reste à un clic.', 'We use a lightweight location hint to preload a short list. Playback is always one tap away.')}
          />
          <div className="grid grid-cols-2 gap-2 min-[520px]:grid-cols-3 sm:gap-3 lg:grid-cols-4 xl:grid-cols-5">
            {channels.length > 0 ? (
              channels.slice(0, 10).map((channel) => (
                <button
                  key={channel.name}
                  type="button"
                  className="group relative flex flex-col overflow-hidden rounded-xl border border-slate-700/80 bg-slate-900/70 p-2.5 text-left shadow-xl ring-1 ring-white/[0.04] transition hover:-translate-y-0.5 hover:border-brand-400/45 hover:bg-slate-900 hover:shadow-[0_18px_45px_rgba(12,74,196,0.24)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500/60 sm:p-3"
                  onClick={() => {
                    setSelectedChannel(channel);
                    nav('/player');
                  }}
                >
                  <div className="pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full bg-brand-400/20 blur-2xl transition-opacity group-hover:opacity-90" />
                  <div className="relative flex aspect-[16/8] w-full items-center justify-center overflow-hidden rounded-lg bg-gradient-to-b from-slate-900 to-slate-950 ring-1 ring-slate-700/80 sm:aspect-[16/9]">
                    <ChannelPreviewMedia logo={channel.logo} t={t} />
                  </div>
                  <h3 className="mt-2 line-clamp-1 text-[14px] font-semibold tracking-tight text-white sm:text-[15px]">{channel.name}</h3>
                  <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-medium text-slate-400">
                    <span className="inline-flex items-center rounded-md border border-slate-700 bg-slate-900/90 px-1.5 py-0.5">
                      {channel?.country || t('Global', 'Global')}
                    </span>
                    <span className="inline-flex items-center rounded-md border border-slate-700 bg-slate-900/90 px-1.5 py-0.5 uppercase">
                      {channel?.language || t('N/D', 'N/A')}
                    </span>
                  </div>
                  {channel.description ? (
                    <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-slate-400">{channel.description}</p>
                  ) : null}
                  <div className="mt-2 flex items-center justify-between border-t border-slate-700/70 pt-2 text-[11px] font-semibold text-slate-300">
                    <span>{t('Regarder', 'Watch')}</span>
                    <span className="text-brand-300 transition-transform group-hover:translate-x-0.5" aria-hidden>→</span>
                  </div>
                </button>
              ))
            ) : (
              <div className="col-span-full rounded-2xl border border-slate-800/80 bg-slate-900/40 px-8 py-16 text-center">
                <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-slate-700 border-t-brand-500" />
                <p className="text-base font-semibold text-slate-200">{t('Préparation de votre sélection régionale', 'Preparing your regional sample')}</p>
                <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500">{t("Veuillez patienter pendant le chargement de l'index des chaînes et de votre localisation approximative.", 'Hang on while we resolve the channel index and your approximate location.')}</p>
              </div>
            )}
          </div>
        </section>

        {/* Languages */}
        <section className="mt-12 border-t border-slate-800/60 pt-10 sm:mt-16 sm:pt-12">
          <SectionHeader
            eyebrow={t('Découverte', 'Discovery')}
            title={t('Parcourir par langue', 'Browse by language')}
            description={t('Accédez directement aux listes de chaînes par langue, sans compte requis.', 'Jump straight to language-specific channel lists with no account required.')}
          />
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 sm:gap-3 md:grid-cols-4 lg:grid-cols-5 lg:gap-4">
            {visibleLanguages.map((language) => (
              <button
                key={language.code}
                type="button"
                onClick={() => openLanguage(language.code)}
                className="group relative overflow-hidden rounded-xl border border-slate-800/80 bg-slate-900/45 p-3.5 text-left transition hover:border-brand-500/35 hover:bg-slate-900/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500/50 sm:rounded-2xl sm:p-4"
              >
                <div className="pointer-events-none absolute -right-8 -top-8 h-20 w-20 rounded-full bg-brand-500/10 opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100" />
                <span className="relative flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg bg-slate-800 ring-1 ring-slate-700/80 sm:h-10 sm:w-10">
                  <img
                    src={`https://flagcdn.com/${language.flagCountryCode}.svg`}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </span>
                <span className="relative mt-3 block line-clamp-1 text-[15px] font-semibold tracking-tight text-white sm:mt-4 sm:text-sm">{isFr ? language.nameFr : language.nameEn}</span>
                <div className="relative mt-2 flex items-center justify-between">
                  <span className="inline-flex items-center rounded-md border border-slate-700/70 bg-slate-900/70 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-400 sm:text-[11px]">
                    {language.code}
                  </span>
                  <span className="text-xs text-brand-300/80 opacity-0 transition duration-200 group-hover:opacity-100" aria-hidden>
                    →
                  </span>
                </div>
              </button>
            ))}
          </div>
          {languages.length > 4 ? (
            <div className="mt-4 flex justify-center sm:mt-5">
              <button
                type="button"
                onClick={() => setShowAllLanguages((prev) => !prev)}
                className="ui-btn-secondary min-h-[42px] rounded-xl px-5 text-sm sm:min-h-[44px] sm:px-6"
              >
                {showAllLanguages ? t('Masquer', 'Hide') : t('Voir plus', 'View more')}
              </button>
            </div>
          ) : null}
        </section>

        {/* Categories */}
        {curatedCategoryCards.length > 0 && (
          <section className="mt-16 border-t border-slate-800/60 pt-12 sm:mt-20 sm:pt-16">
            <SectionHeader
              eyebrow={t('Collections', 'Collections')}
              title={t('Catégories sélectionnées', 'Curated categories')}
              description={t("Listes maintenues dans l'index des chaînes : choisissez-en une pour ouvrir le catalogue correspondant.", 'Lists maintained in the channel index. Pick one to open the matching catalog.')}
            />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 lg:gap-4">
              {visibleCategoryCards.map((category) => (
                <button
                  key={category.key}
                  type="button"
                  onClick={() => openCategory(category.key)}
                  aria-label={t(`Ouvrir la liste ${category.label} avec ${category.count} chaînes`, `Open ${category.label} list with ${category.count} channels`)}
                  className="group relative overflow-hidden rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5 text-center transition duration-300 hover:-translate-y-0.5 hover:border-indigo-400/50 hover:shadow-[0_10px_30px_rgba(30,64,175,0.18)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400/60 lg:min-h-[148px]"
                >
                  <img
                    src={category.image}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = `https://picsum.photos/seed/tv-fallback-${category.key}/960/640`;
                    }}
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-65 transition duration-500 group-hover:scale-105 group-hover:opacity-75"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.12)_0%,rgba(2,6,23,0.58)_55%,rgba(2,6,23,0.82)_100%)]" />
                  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(99,102,241,0.22),transparent_55%)]" />

                  <div className="relative z-10 flex flex-col items-center">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-indigo-200/90">{t('Catégorie', 'Category')}</span>
                    <span className="mt-2 block text-lg font-bold tracking-tight text-white sm:text-xl">{category.label}</span>
                    <span className="mt-3 inline-flex items-center rounded-full border border-white/15 bg-slate-950/45 px-2.5 py-1 text-[11px] font-semibold text-slate-200 ring-1 ring-white/5">
                      {category.count.toLocaleString()} {t('chaînes', 'channels')}
                    </span>
                  </div>

                  <span className="relative z-10 mt-4 inline-flex items-center text-xs font-medium text-slate-300">
                    {t('Ouvrir la liste', 'Open list')} <span className="ml-1 transition-transform group-hover:translate-x-0.5" aria-hidden>→</span>
                  </span>
                </button>
              ))}
            </div>
            {curatedCategoryCards.length > 6 ? (
              <div className="mt-5 flex justify-center">
                <button
                  type="button"
                  onClick={() => setShowAllCategories((prev) => !prev)}
                  className="ui-btn-secondary min-h-[44px] px-6 text-sm"
                >
                  {showAllCategories ? t('Masquer', 'Hide') : t('Voir plus', 'View more')}
                </button>
              </div>
            ) : null}
          </section>
        )}

        {/* How it works */}
        <section id="how-it-works" className="mt-14 scroll-mt-20 border-t border-slate-800/60 pt-10 sm:mt-24 sm:scroll-mt-24 sm:pt-20">
          <SectionHeader
            eyebrow={t('Parcours', 'Workflow')}
            title={t('De la recherche à la lecture en trois étapes', 'From browse to playback in three steps')}
            description={t('Gardez vos repères entre la grille, les listes et le lecteur : chaque écran a un rôle clair.', 'Keep orientation as you move between the grid, lists, and player. Each screen has one clear job.')}
          />
          <div className="grid gap-2.5 sm:gap-4 md:grid-cols-3 md:gap-6">
            {[
              {
                step: '01',
                title: t('Choisir une zone', 'Choose geography'),
                body: t('Filtrez les pays par région et par nom. Votre sélection met à jour la liste de chaînes à droite.', 'Filter countries by region and name. Your selection drives the channel list on the right.'),
                actionLabel: t("Ouvrir l'explorateur", 'Open browser'),
                onClick: onEnter,
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                ),
              },
              {
                step: '02',
                title: t('Affiner la liste', 'Refine the list'),
                body: t('Utilisez les langues ou catégories instantanément. La recherche filtre les pays et les chaînes.', 'Use language shortcuts or categories instantly. Search narrows both countries and channels.'),
                actionLabel: t('Ouvrir les chaînes', 'Open channels'),
                onClick: openAllChannels,
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16M4 12h16M4 18h7" />
                ),
              },
              {
                step: '03',
                title: t('Regarder en mode focus', 'Watch in focus'),
                body: t('Ouvrez le lecteur pour les flux HLS ou intégrés. Changez de chaîne sans perdre le contexte.', 'Open the player for HLS or embedded streams. Swap related channels without losing context.'),
                actionLabel: t('Aller au lecteur', 'Go to player'),
                onClick: openPlayerWithSample,
                icon: (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                ),
              },
            ].map((item) => (
              <button
                key={item.step}
                type="button"
                onClick={item.onClick}
                className="group relative rounded-xl border border-slate-800/80 bg-slate-900/30 p-4 text-left transition hover:border-slate-700 hover:bg-slate-900/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500/50 sm:rounded-2xl sm:p-6"
              >
                <span className="text-[11px] font-bold tabular-nums text-brand-500/80 sm:text-xs">{item.step}</span>
                <div className="mt-3 flex h-9 w-9 items-center justify-center rounded-lg bg-slate-800/80 text-brand-400 ring-1 ring-slate-700/80 sm:mt-4 sm:h-11 sm:w-11 sm:rounded-xl">
                  <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                    {item.icon}
                  </svg>
                </div>
                <h3 className="mt-3 text-[1.35rem] font-semibold leading-tight text-white sm:mt-4 sm:text-lg">{item.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-500 sm:mt-2 sm:text-sm">{item.body}</p>
                <span className="mt-3 inline-flex items-center text-[11px] font-semibold text-brand-300 transition group-hover:text-brand-200 sm:mt-4 sm:text-xs">
                  {item.actionLabel}
                  <span className="ml-1 transition-transform group-hover:translate-x-0.5" aria-hidden>→</span>
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="mt-24">
          <div className="relative overflow-hidden rounded-3xl border border-slate-800/80 bg-gradient-to-br from-slate-900/90 via-slate-950 to-slate-900/90 px-6 py-12 text-center sm:px-12 sm:py-16">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(42,158,244,0.12),transparent_55%)]" />
            <div className="relative mx-auto max-w-2xl">
              <h2 className="text-2xl font-bold tracking-tight text-white sm:text-3xl">{t('Prêt à explorer ?', 'Ready to explore?')}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base">
                {t("L'explorateur de pays est optimisé pour un repérage rapide : recherche clavier, filtres régionaux et colonne dédiée aux chaînes.", 'The country browser is optimized for quick scanning: keyboard search, region filters, and a dedicated channel column.')}
              </p>
              <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:justify-center">
                <button type="button" onClick={onEnter} className="ui-btn-primary min-h-[48px] px-10 text-base">
                  {t("Aller à l'explorateur de pays", 'Go to country browser')}
                </button>
                <button type="button" onClick={openAllChannels} className="ui-btn-secondary min-h-[48px] px-10 text-base">
                  {t('Liste des chaînes', 'Channel list')}
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-slate-800/80 bg-slate-950/95">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-10 sm:flex-row sm:items-start sm:justify-between sm:px-6 lg:px-8">
          <div className="max-w-md">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-indigo-600 text-[10px] font-bold text-white">AT</span>
              <span className="text-sm font-semibold text-white">ASVA TV</span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-500">
              {t('Interface de découverte pour les flux publiquement indexés. La disponibilité et les droits varient selon les diffuseurs et les régions.', 'Discovery interface for publicly indexed streams. Availability and rights vary by broadcaster and region.')}
            </p>
          </div>
          <div className="flex flex-col gap-4 text-sm sm:items-end">
            <a href="mailto:admin@asva.tv" className="font-medium text-brand-400 transition hover:text-brand-300">
              admin@asva.tv
            </a>
            <p className="text-xs text-slate-600">© {new Date().getFullYear()} ASVA TV</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
